import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { updateFoundingPartnerProfile } from "../../redux/actions/dataActions";
import axios from "axios";

// MUI components
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Avatar,
  Box,
  Typography,
  IconButton,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import CloseIcon from "@mui/icons-material/Close";

const useStyles = makeStyles()((theme) => ({
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  avatarContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBottom: theme.spacing(3),
  },
  avatar: {
    width: 120,
    height: 120,
    marginBottom: theme.spacing(2),
  },
  uploadButton: {
    marginTop: theme.spacing(1),
  },
  input: {
    display: "none",
  },
  textField: {
    marginBottom: theme.spacing(2),
  },
  progress: {
    marginRight: theme.spacing(1),
  },
  uploadProgress: {
    marginTop: theme.spacing(1),
  },
}));

function EditFoundingPartnerProfileModal(props) {
  const { classes } = useStyles();
  const { open, onClose, foundingPartnerDetails } = props;

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [company, setCompany] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [bio, setBio] = useState("");
  const [profileImageUrl, setProfileImageUrl] = useState("");
  const [showOnPublicPage, setShowOnPublicPage] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState({});

  const fileInputRef = useRef(null);

  // Initialize form with existing data
  useEffect(() => {
    if (foundingPartnerDetails) {
      setFirstName(foundingPartnerDetails.firstName || "");
      setLastName(foundingPartnerDetails.lastName || "");
      setCompany(foundingPartnerDetails.company || "");
      setWebsiteUrl(foundingPartnerDetails.websiteUrl || "");
      setBio(foundingPartnerDetails.bio || "");
      setProfileImageUrl(foundingPartnerDetails.profileImageUrl || "");
      setImagePreview(foundingPartnerDetails.profileImageUrl || "");
      setShowOnPublicPage(foundingPartnerDetails.showOnPublicPage || false);
    }
  }, [foundingPartnerDetails, open]);

  // Update errors from Redux
  useEffect(() => {
    if (props.UI.errors) {
      setErrors(props.UI.errors);
    }
  }, [props.UI.errors]);

  const handleImageSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        setErrors({ ...errors, image: "Please select an image file" });
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setErrors({ ...errors, image: "Image must be less than 5MB" });
        return;
      }

      setImageFile(file);
      setErrors({ ...errors, image: null });

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async () => {
    if (!imageFile) {
      return profileImageUrl;
    }

    setUploading(true);
    try {
      // Create FormData for multipart upload
      const formData = new FormData();
      formData.append("image", imageFile);

      // Include old image URL so backend can delete it
      if (profileImageUrl) {
        formData.append("oldImageUrl", profileImageUrl);
      }

      // Upload to backend
      const response = await axios.post(
        "/api/foundingPartners/uploadImage",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      setUploading(false);
      return response.data.imageUrl;
    } catch (error) {
      console.error("Error uploading image:", error);
      setErrors({
        ...errors,
        image: error.response?.data?.error || "Error uploading image",
      });
      setUploading(false);
      throw error;
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Validate required fields
    if (!firstName.trim() || !lastName.trim()) {
      setErrors({
        ...errors,
        firstName: !firstName.trim() ? "First name is required" : null,
        lastName: !lastName.trim() ? "Last name is required" : null,
      });
      return;
    }

    try {
      // Upload image if one was selected
      let imageUrl = profileImageUrl;
      if (imageFile) {
        imageUrl = await uploadImage();
      }

      // Prepare profile data
      const profileData = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        company: company.trim(),
        websiteUrl: websiteUrl.trim(),
        bio: bio.trim(),
        profileImageUrl: imageUrl,
        showOnPublicPage: showOnPublicPage,
      };

      // Submit the profile update
      await props.updateFoundingPartnerProfile(profileData);

      // Close modal on success
      onClose(true);
    } catch (error) {
      console.error("Error updating profile:", error);
    }
  };

  const handleClose = () => {
    // Reset form
    setImageFile(null);
    setImagePreview("");
    setErrors({});
    onClose(false);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="edit-profile-dialog"
    >
      <DialogTitle className={classes.dialogTitle}>
        <span>Edit Your Public Profile</span>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          {/* Profile Image Upload */}
          <div className={classes.avatarContainer}>
            <Avatar src={imagePreview} alt="Profile" className={classes.avatar}>
              {!imagePreview && firstName.charAt(0).toUpperCase()}
            </Avatar>
            <input
              accept="image/*"
              className={classes.input}
              id="profile-image-upload"
              type="file"
              onChange={handleImageSelect}
              ref={fileInputRef}
            />
            <label htmlFor="profile-image-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<PhotoCamera />}
                className={classes.uploadButton}
              >
                Upload Profile Picture
              </Button>
            </label>
            {errors.image && (
              <Typography color="error" variant="caption">
                {errors.image}
              </Typography>
            )}
            {uploading && (
              <Box className={classes.uploadProgress}>
                <CircularProgress size={20} />
                <Typography variant="caption"> Uploading...</Typography>
              </Box>
            )}
          </div>

          {/* Form Fields */}
          <TextField
            label="First Name"
            variant="outlined"
            fullWidth
            required
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            error={!!errors.firstName}
            helperText={errors.firstName}
            className={classes.textField}
          />

          <TextField
            label="Last Name"
            variant="outlined"
            fullWidth
            required
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            error={!!errors.lastName}
            helperText={errors.lastName}
            className={classes.textField}
          />

          <TextField
            label="Company Name"
            variant="outlined"
            fullWidth
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            error={!!errors.company}
            helperText={errors.company}
            className={classes.textField}
          />

          <TextField
            label="Website URL"
            variant="outlined"
            fullWidth
            placeholder="https://example.com"
            value={websiteUrl}
            onChange={(e) => setWebsiteUrl(e.target.value)}
            error={!!errors.websiteUrl}
            helperText={errors.websiteUrl}
            className={classes.textField}
          />

          <TextField
            label="Bio"
            variant="outlined"
            fullWidth
            multiline
            rows={4}
            placeholder="Tell us about yourself and what you do..."
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            error={!!errors.bio}
            helperText={errors.bio || `${bio.length}/500 characters`}
            inputProps={{ maxLength: 500 }}
            className={classes.textField}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={showOnPublicPage}
                onChange={(e) => setShowOnPublicPage(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body1">
                  Display my profile on the public Founding Partners page
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Toggle this off if you prefer to keep your profile private
                </Typography>
              </Box>
            }
            className={classes.textField}
          />

          {errors.general && (
            <Typography color="error" variant="body2" gutterBottom>
              {errors.general}
            </Typography>
          )}
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={props.UI.loading || uploading}
        >
          {(props.UI.loading || uploading) && (
            <CircularProgress size={20} className={classes.progress} />
          )}
          Save Profile
        </Button>
      </DialogActions>
    </Dialog>
  );
}

EditFoundingPartnerProfileModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  foundingPartnerDetails: PropTypes.object,
  updateFoundingPartnerProfile: PropTypes.func.isRequired,
  UI: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  UI: state.UI,
});

export default connect(mapStateToProps, { updateFoundingPartnerProfile })(
  EditFoundingPartnerProfileModal
);
